import {
  createFileRoute,
  notFound,
  redirect,
  useLoaderData,
} from "@tanstack/react-router";
import { getLocation } from "~/lib/server-functions/get-locations";
import { checkLocationPreviewAuthorization } from "~/lib/server-functions/check-location-preview-authorization";
import {
  getBaseUrl,
  resolveAssetUrlServer,
} from "~/lib/utils";
import { getLocationPath } from "~/lib/utils/location-path";
import { LocationPageContent } from "~/routes/__main/location/$id";
import type { LocationPageLoaderData } from "~/routes/__main/location/$id";
import { seo } from "~/utils/seo";

type LocationSearch = {
  previewPlan?: "superior" | "premium" | "basic";
};

export const Route = createFileRoute("/__main/$category/$slug/$locationId/$tab")(
  {
    validateSearch: (search: Record<string, unknown>): LocationSearch => {
      const validPlans = ["superior", "premium", "basic"];
      const previewPlan = search.previewPlan as string | undefined;
      return {
        previewPlan:
          previewPlan && validPlans.includes(previewPlan)
            ? (previewPlan as LocationSearch["previewPlan"])
            : undefined,
      };
    },
    component: CanonicalLocationRouteComponent,
    loader: async ({ params, location }) => {
      const locationIdParam = params.locationId;
      const match = /^LE-(\d+)$/.exec(locationIdParam);
      if (!match) {
        throw notFound();
      }
      const id = parseInt(match[1], 10);
      const locationData = await getLocation({ data: id });

      if (!locationData) {
        throw notFound();
      }

      let authorizedPreviewPlan: LocationSearch["previewPlan"] = undefined;
      const searchParams = new URLSearchParams(location.search);
      const previewPlanParam = searchParams.get("previewPlan");
      const validPlans: LocationSearch["previewPlan"][] = [
        "superior",
        "premium",
        "basic",
      ];
      const isValidPlanParam = (
        param: string | null
      ): param is NonNullable<LocationSearch["previewPlan"]> =>
        param !== null &&
        validPlans.includes(param as LocationSearch["previewPlan"]);

      if (isValidPlanParam(previewPlanParam) && locationData) {
        const authorizedPlan = await checkLocationPreviewAuthorization({
          data: { locationId: id, previewPlan: previewPlanParam },
        });
        if (authorizedPlan) {
          authorizedPreviewPlan = authorizedPlan;
        }
      }

      const isPreviewMode =
        locationData != null && locationData.status !== "published";

      const tab = parseInt(params.tab, 10);
      const initialTab = isNaN(tab) ? 0 : tab;
      const canonicalPath = getLocationPath(locationData, initialTab);
      const [, canonicalCategory, canonicalSlug] = canonicalPath.split("/");

      // Redirect when category or slug don't match (e.g. old link with pre-migration
      // location type in the category segment; see LOCATION_TYPE_OLD_TO_NEW in ~/lib/location-type-map).
      if (
        params.category !== canonicalCategory ||
        params.slug !== canonicalSlug
      ) {
        throw redirect({ to: canonicalPath });
      }

      return {
        location: locationData,
        previewPlan: authorizedPreviewPlan,
        isPreviewMode,
        initialTab,
      } satisfies LocationPageLoaderData;
    },
    head: ({ loaderData }) => {
      const loc = loaderData?.location;
      const name = loc?.nameForm?.german || loc?.name || "Location";
      const city = loc?.city;
      const title = city
        ? `${name} | ${city} | Locationguide24`
        : `${name} | Locationguide24`;
      const rawDescription =
        loc?.descriptionForm?.german || loc?.description || "";
      const description =
        rawDescription.length > 160
          ? `${rawDescription.slice(0, 157).trim()}…`
          : rawDescription || undefined;
      const initialTab = loaderData?.initialTab ?? 0;
      const canonicalPath = loc
        ? getLocationPath(loc, initialTab)
        : undefined;
      const url = canonicalPath
        ? `${getBaseUrl()}${canonicalPath}`
        : undefined;
      const ogImage =
        loc?.images?.find(
          (img) =>
            img.mimetype &&
            !img.mimetype.startsWith("video") &&
            img.purpose === "location_gallery_image",
        ) ?? loc?.images?.[0];
      const image = ogImage?.path
        ? resolveAssetUrlServer(ogImage.path)
        : undefined;
      return {
        meta: [
          ...seo({
            title,
            description:
              description ||
              `${name}${city ? ` in ${city}` : ""} – Eventlocation, Hochzeitslocation oder Tagungsraum bei Locationguide24`,
            url,
            image,
            type: "article",
          }),
        ],
        ...(url ? { links: [{ rel: "canonical" as const, href: url }] } : {}),
      };
    },
  }
);

function CanonicalLocationRouteComponent() {
  const loaderData = useLoaderData({
    from: "/__main/$category/$slug/$locationId/$tab",
  });
  return <LocationPageContent loaderData={loaderData} />;
}
